import type { JSX, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { HeroUIProvider, HeroUIProviderProps, ToastProvider } from '@heroui/react';

import { THEME_OPTIONS, ThemeContext, type ThemeName } from './theme';

interface ThemeProviderProps {
  children: ReactNode;
  heroUIProps?: Omit<HeroUIProviderProps, 'children'>;
}

// Maps (themeName, isDarkMode) to the single DOM class HeroUI/theme.css
// expect. 'default' is special-cased to match HeroUI's own pre-existing
// `light`/`dark` keys; every other ThemeName is derived by convention so
// adding a theme never requires touching this function — see THEME_OPTIONS
// and the matching `{value}Light`/`{value}Dark` HeroUI/CSS keys.
export function resolveThemeClass(themeName: ThemeName, isDarkMode: boolean): string {
  if (themeName === 'default') {
    return isDarkMode ? 'dark' : '';
  }
  return `${themeName}${isDarkMode ? 'Dark' : 'Light'}`;
}

// Every class resolveThemeClass can ever produce (plus the base 'dark'),
// derived from THEME_OPTIONS so it stays correct as themes are added. dark/
// charcoalLight/charcoalDark etc. are mutually exclusive — all must be
// removed before the new target class is added, or switching themes can
// leave a stale class behind.
const ALL_THEME_CLASSES = [
  'dark',
  ...THEME_OPTIONS.filter(option => option.value !== 'default').flatMap(option => [
    `${option.value}Light`,
    `${option.value}Dark`,
  ]),
];

export function ThemeProvider({ children, heroUIProps }: ThemeProviderProps): JSX.Element {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check local storage for saved preference
    const saved = localStorage.getItem('darkMode');
    // Check system preference if no saved preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    return saved ? saved === 'true' : prefersDark;
  });

  const [themeName, setThemeName] = useState<ThemeName>(() => {
    const saved = localStorage.getItem('themeName');
    const isValidThemeName = THEME_OPTIONS.some(option => option.value === saved);
    return isValidThemeName ? (saved as ThemeName) : 'default';
  });

  useEffect(() => {
    // Recompute and apply the single active theme class. Always clear every
    // candidate class first — dark/charcoalLight/charcoalDark etc. are
    // mutually exclusive, never toggled independently.
    ALL_THEME_CLASSES.forEach(className => document.documentElement.classList.remove(className));
    const targetClass = resolveThemeClass(themeName, isDarkMode);
    if (targetClass) {
      document.documentElement.classList.add(targetClass);
    }
    // Save preferences to local storage
    localStorage.setItem('darkMode', isDarkMode.toString());
    localStorage.setItem('themeName', themeName);
  }, [isDarkMode, themeName]);

  const toggleDarkMode = (): void => {
    setIsDarkMode(prev => !prev);
  };

  const contextValue = useMemo(
    () => ({ isDarkMode, toggleDarkMode, themeName, setThemeName }),
    [isDarkMode, themeName],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <HeroUIProvider {...heroUIProps}>
        <ToastProvider toastProps={{ shouldShowTimeoutProgress: true }} />
        {children}
      </HeroUIProvider>
    </ThemeContext.Provider>
  );
}
